import {
    EventResponseStatus,
    EventResponseStatusRow,
    RsvpRosterCounts,
    RsvpStatus,
} from 'src/domain/event';

/** `unanswered` holds Waiting for reply before the deadline and No response
 * after it. */
export type ResponseStatusGroup = 'going' | 'unanswered' | 'notGoing';

export const RESPONSE_STATUS_GROUPS: ResponseStatusGroup[] = [
    'going',
    'unanswered',
    'notGoing',
];

const getResponseStatusGroup = (status: RsvpStatus): ResponseStatusGroup =>
    status === 'going' || status === 'notGoing' ? status : 'unanswered';

export const groupResponseStatusRows = (
    rows: EventResponseStatusRow[],
): Record<ResponseStatusGroup, EventResponseStatusRow[]> => {
    const groups: Record<ResponseStatusGroup, EventResponseStatusRow[]> = {
        going: [],
        unanswered: [],
        notGoing: [],
    };
    rows.forEach(row => groups[getResponseStatusGroup(row.status)].push(row));
    return groups;
};

export const getResponseStatusGroupCounts = (
    counts: RsvpRosterCounts,
): Record<ResponseStatusGroup, number> => ({
    going: counts.going,
    unanswered: counts.waitingForReply + counts.noResponse,
    notGoing: counts.notGoing,
});

/** Uses the server's closed state, never the browser clock. */
export const getUnansweredStatus = ({
    closed,
}: Pick<EventResponseStatus, 'closed'>): 'waitingForReply' | 'noResponse' =>
    closed ? 'noResponse' : 'waitingForReply';

export const getResponseStatusResidentLabel = (
    row: EventResponseStatusRow,
): string =>
    row.residents
        .map(resident => resident.name)
        .filter(Boolean)
        .join(', ');

/** Sort by the displayed resident names, then invitee name. Missing residents go last. */
export const sortResponseStatusRowsByResident = (
    rows: EventResponseStatusRow[],
    locale: string,
): EventResponseStatusRow[] => {
    const collator = new Intl.Collator(locale, {
        sensitivity: 'base',
        numeric: true,
    });
    return rows
        .map(row => ({
            row,
            residentLabel: getResponseStatusResidentLabel(row),
        }))
        .sort((a, b) => {
            if (Boolean(a.residentLabel) !== Boolean(b.residentLabel)) {
                return a.residentLabel ? -1 : 1;
            }
            return (
                collator.compare(a.residentLabel, b.residentLabel) ||
                collator.compare(a.row.name ?? '', b.row.name ?? '') ||
                collator.compare(a.row.inviteeId, b.row.inviteeId)
            );
        })
        .map(({ row }) => row);
};
